import { SettingsPermissions } from "@redotech/account-common/permissions";
import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import {
  MAX_MAX_QUESTIONS,
  MIN_MAX_QUESTIONS,
} from "@redotech/redo-agents-cards/returns-agents/util/return-reason-conversation-util";
import { aiReasonsDefaultMaxQuestions } from "@redotech/redo-agents-common/returns-agents/return-reason-categorization";
import { useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { isSingleBranchAiReasonsFlowType } from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  AiReturnReasonsState,
  CustomGuidanceState,
} from "@redotech/redo-return-ui/flows/return-flow/ai-return-reasons";
import * as multipleChoiceCss from "@redotech/redo-return-ui/flows/return-flow/multiple-choice.module.css";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import {
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
} from "react";
import { useFieldEditable } from "../flow-editor/flow-readonly-context";
import { AdditionalAiInfo } from "./multiple-choice";
import {
  customExplanationSubtextFieldSpec,
  customQuestionFieldSpec,
} from "./translations/field-specs";
import { StepTranslationLanguages } from "./translations/step-translation-languages";
import {
  stepElementIdsOf,
  StepTranslationElement,
} from "./translations/step-translations";
import { TranslatableTextareaField } from "./translations/translatable-textarea-field";

type StepSelectFn = StepTypeDetailsProps<AiReturnReasonsState>["stepSelect"];

export const AiReturnReasonsDetails = memo(function AiReturnReasonsDetails({
  stepSelect,
  state,
  setState,
  flowType,
}: StepTypeDetailsProps<AiReturnReasonsState>) {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const canEditReturnsSettings = useFieldEditable(
    useHasPermission(SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS),
  );
  const isRedoUser =
    (user?.email && /@getredo\.com/.test(user?.email)) || false;
  // Warranty flows: one route out, no verdict branches, always AI-generated
  // categories, and a deeper default question limit.
  const singleBranch = isSingleBranchAiReasonsFlowType(flowType);
  const defaultMaxQuestions = aiReasonsDefaultMaxQuestions(
    singleBranch ? "warranty" : "return",
  );

  const predefinedCategoriesLink = useMemo(
    () =>
      `${window.location.href.split("/settings")[0]}/settings/returns?tab=operations&group=customer_portal.predefined_categories&setting=returns/aiReturnReasons/usePredefinedCategories&highlight=both`,
    [],
  );

  const nextStepChange = useHandler((next?: StepId) => {
    setState((state) => ({ ...state, next }));
  });

  const nextWrongProductStepChange = useHandler((nextWrongProduct?: StepId) => {
    setState((state) => ({ ...state, nextWrongProduct }));
  });

  const nextDamagedStepChange = useHandler((nextDamaged?: StepId) => {
    setState((state) => ({ ...state, nextDamaged }));
  });

  const handleAdditionalAiInfo = useHandler(
    (additionalAiInfo: string | undefined) =>
      setState((state) => ({ ...state, additionalAiInfo })),
  );

  const handleMaxQuestionsChange = useHandler(
    (maxQuestions: number | undefined) => {
      setState((state) => ({ ...state, maxQuestions }));
    },
  );

  const handleGuidanceChange = useHandler(
    (merchantGuidance: string | undefined) => {
      setState((state) => ({
        ...state,
        merchantGuidance: merchantGuidance || undefined,
      }));
    },
  );

  const handleCustomQuestionChange = useHandler(
    (customQuestion: string | undefined) => {
      setState((state) => ({
        ...state,
        customQuestion: customQuestion || undefined,
      }));
    },
  );

  const handleCustomExplanationSubtextChange = useHandler(
    (customExplanationSubtext: string | undefined) => {
      setState((state) => ({
        ...state,
        customExplanationSubtext: customExplanationSubtext || undefined,
      }));
    },
  );

  const handleAddCustomGuidance = useHandler(() =>
    setState((state) =>
      produce(state, (draft) => {
        if (!draft.customGuidance) {
          draft.customGuidance = [];
        }
        draft.customGuidance.push({
          id: crypto.randomUUID(),
          description: "",
          next: undefined,
        });
      }),
    ),
  );

  function getMaxQuestionsValidationErrors(maxQuestions: number | undefined) {
    if (maxQuestions === undefined) {
      return [];
    }
    if (maxQuestions < MIN_MAX_QUESTIONS) {
      return [
        t("Must be greater than {{minimum}}", {
          minimum: MIN_MAX_QUESTIONS - 1,
        }),
      ];
    }
    if (maxQuestions > MAX_MAX_QUESTIONS) {
      return [
        t("Must be less than or equal to {{maximum}}", {
          maximum: MAX_MAX_QUESTIONS,
        }),
      ];
    }
    return [];
  }

  const customGuidance = state.customGuidance ?? [];

  const translationElements = useMemo<StepTranslationElement[]>(
    () => [
      {
        elementId: state.stepUUID,
        field: customQuestionFieldSpec(state.customQuestion || ""),
      },
      {
        elementId: state.stepUUID,
        field: customExplanationSubtextFieldSpec(
          state.customExplanationSubtext || "",
        ),
      },
    ],
    [state.stepUUID, state.customQuestion, state.customExplanationSubtext],
  );
  const stepElementIds = useMemo(
    () => stepElementIdsOf(translationElements),
    [translationElements],
  );

  return (
    <>
      <Flex dir="column" gap="xl">
        {!singleBranch && (
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {t("Guidance")}
            </Text>
            <Text fontSize="xxs" textColor="secondary">
              {t(
                "Define how the AI should route customers based on their return reason. The first two rules are built-in.",
              )}
            </Text>
          </Flex>
        )}

        {!singleBranch && (
          <>
            <BuiltInGuidanceCard
              handleIndex={0}
              ifText={t("Product is damaged")}
              required
              requiredMissing={!state.nextDamaged}
              stepSelect={stepSelect}
              value={state.nextDamaged}
              valueChange={nextDamagedStepChange}
            />
            <BuiltInGuidanceCard
              handleIndex={1}
              ifText={t("Wrong product")}
              required
              requiredMissing={!state.nextWrongProduct}
              stepSelect={stepSelect}
              value={state.nextWrongProduct}
              valueChange={nextWrongProductStepChange}
            />

            <IterableMap items={customGuidance} keyFn={(g) => g.id}>
              {(guidance, index) => (
                <CustomGuidanceCardComponent
                  canEditReturnsSettings={canEditReturnsSettings}
                  guidance={guidance}
                  index={index}
                  setState={setState}
                  stepSelect={stepSelect}
                />
              )}
            </IterableMap>

            <Flex align="flex-start">
              <RedoButton
                disabled={!canEditReturnsSettings}
                hierarchy="secondary"
                IconLeading={PlusIcon}
                onClick={handleAddCustomGuidance}
                size="sm"
                text={t("Add guidance")}
              />
            </Flex>

            <Divider />
          </>
        )}

        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            {singleBranch ? t("Next step") : t("All other cases go...")}
          </Text>
          {stepSelect({
            value: state.next,
            valueChange: nextStepChange,
            handleIndex: singleBranch ? 0 : 2 + customGuidance.length,
          })}
          {!state.next && (
            <Text fontSize="xs" fontWeight="regular" textColor="error">
              {t("Required")}
            </Text>
          )}
        </Flex>

        <Divider />

        <StepTranslationLanguages
          canEdit={canEditReturnsSettings}
          elements={translationElements}
          flowType={flowType}
        />
        <TranslatableTextareaField
          canEdit={canEditReturnsSettings}
          elementId={state.stepUUID}
          field={customQuestionFieldSpec(state.customQuestion || "")}
          flowType={flowType}
          helperText={t(
            "The initial question shown to customers. Leave empty to use the default question. Make sure to not bias the customer with a leading question to ensure data is as accurate as possible.",
          )}
          placeholder={
            singleBranch
              ? t("What issue are you experiencing with this item?")
              : t("Why are you returning this item?")
          }
          setValue={handleCustomQuestionChange}
          stepElementIds={stepElementIds}
        />
        <Divider />
        <TranslatableTextareaField
          canEdit={canEditReturnsSettings}
          elementId={state.stepUUID}
          field={customExplanationSubtextFieldSpec(
            state.customExplanationSubtext || "",
          )}
          flowType={flowType}
          helperText={t(
            "The subtext shown below the main question. Leave empty to use the default subtext. Make sure to not bias the customer with a leading question to ensure data is as accurate as possible.",
          )}
          placeholder={
            singleBranch
              ? t(
                  "Please describe the problem, when it started, and how it happened.",
                )
              : t("Please explain in detail why you are returning this item.")
          }
          setValue={handleCustomExplanationSubtextChange}
          stepElementIds={stepElementIds}
        />
      </Flex>
      <Divider />

      <RedoCollapseSubsection title={t("Advanced options")}>
        {!singleBranch && (
          <>
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" textColor="secondary">
                {t("Predefined categories")}
              </Text>
              <Text fontSize="xxs" textColor="secondary">
                {t(
                  "Optionally define categories for the AI to sort return reasons into.",
                )}
              </Text>
              <RedoExternalLink
                fontSize="md"
                text={t("Manage predefined categories")}
                url={predefinedCategoriesLink}
              />
            </Flex>
            <Divider />
          </>
        )}
        <Text fontSize="xs" textColor="secondary">
          {t("Maximum questions")}
        </Text>
        {getMaxQuestionsValidationErrors(state.maxQuestions).map(
          (error, index) => (
            <Text
              fontSize="xs"
              fontWeight="regular"
              key={index}
              textColor="error"
            >
              {error}
            </Text>
          ),
        )}

        <RedoTextInput
          max={MAX_MAX_QUESTIONS}
          min={MIN_MAX_QUESTIONS}
          placeholder={String(defaultMaxQuestions)}
          setValue={(value: string) => {
            const numValue = parseInt(value, 10);
            if (!isNaN(numValue)) {
              handleMaxQuestionsChange(numValue);
            } else {
              handleMaxQuestionsChange(undefined);
            }
          }}
          state={canEditReturnsSettings ? "default" : "readonly"}
          step={1}
          value={String(state.maxQuestions ?? "")}
        />
        <Text fontSize="xxs" textColor="secondary">
          {t(
            "Maximum number of questions the AI can ask the customer (default: {{defaultMaximum}}, min: {{minimum}}, max: {{maximum}}). Set to 1 to categorize after a single question with no follow-ups.",
            {
              defaultMaximum: defaultMaxQuestions,
              minimum: MIN_MAX_QUESTIONS,
              maximum: MAX_MAX_QUESTIONS,
            },
          )}
        </Text>
        <Text fontSize="xs" textColor="secondary">
          {t("Guidance")}
        </Text>
        <RedoTextareaInputField
          readOnly={!canEditReturnsSettings}
          setValue={handleGuidanceChange}
          value={state.merchantGuidance || ""}
        />
        <Text fontSize="xxs" textColor="secondary">
          {t("Additional instructions for the AI to follow")}
        </Text>
      </RedoCollapseSubsection>
      {isRedoUser ? (
        <AdditionalAiInfo
          canEditReturnsSettings={canEditReturnsSettings}
          collapsedTitle={t("Redo user advanced options")}
          currentAdditionalAiInfo={state.additionalAiInfo}
          setAdditionalAiInfo={handleAdditionalAiInfo}
        />
      ) : null}
    </>
  );
});

const BuiltInGuidanceCard = memo(function BuiltInGuidanceCard({
  ifText,
  stepSelect,
  value,
  valueChange,
  handleIndex,
  required,
  requiredMissing,
}: {
  ifText: string;
  stepSelect: StepSelectFn;
  value: StepId | undefined;
  valueChange: (next: StepId | undefined) => void;
  handleIndex: number;
  required?: boolean;
  requiredMissing?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <Flex className={multipleChoiceCss.optionCard} dir="column" gap="3xl">
      <Flex dir="column" gap="none">
        <Text fontSize="xs" textColor="secondary">
          {t("If...")}
        </Text>
        <RedoTextInput setValue={() => {}} state="readonly" value={ifText} />
      </Flex>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" textColor="secondary">
          {t("Then...")}
        </Text>
        {stepSelect({ value, valueChange, handleIndex })}
        {required && requiredMissing && (
          <Text fontSize="xs" fontWeight="regular" textColor="error">
            {t("Required")}
          </Text>
        )}
      </Flex>
    </Flex>
  );
});

const CustomGuidanceCardComponent = memo(function CustomGuidanceCardComponent({
  canEditReturnsSettings,
  guidance,
  index,
  setState,
  stepSelect,
}: {
  canEditReturnsSettings: boolean;
  guidance: CustomGuidanceState;
  index: number;
  setState: Dispatch<SetStateAction<AiReturnReasonsState>>;
  stepSelect: StepSelectFn;
}) {
  const { t } = useTranslation();
  const setDescription = useHandler((description: string) =>
    setState((s) =>
      produce(s, (draft) => {
        if (draft.customGuidance?.[index]) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          draft.customGuidance[index]!.description = description;
        }
      }),
    ),
  );
  const setNext = useHandler((next: StepId | undefined) =>
    setState((s) =>
      produce(s, (draft) => {
        if (draft.customGuidance?.[index]) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          draft.customGuidance[index]!.next = next;
        }
      }),
    ),
  );
  const handleDelete = useHandler(() =>
    setState((s) =>
      produce(s, (draft) => {
        draft.customGuidance?.splice(index, 1);
      }),
    ),
  );

  return (
    <Flex className={multipleChoiceCss.optionCard} dir="column" gap="md">
      <Flex align="center" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {t("Custom guidance")}
        </Text>
        {canEditReturnsSettings && (
          <RedoButton
            aria-label={t("Delete guidance")}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={(event) => {
              handleDelete();
              event.stopPropagation();
            }}
            size="sm"
            theme="destructive"
          />
        )}
      </Flex>
      <Flex dir="column" gap="3xl">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            {t("If...")}
          </Text>
          <RedoTextInput
            placeholder={t("e.g. The product doesn't fit")}
            setValue={setDescription}
            state={!canEditReturnsSettings ? "readonly" : undefined}
            value={guidance.description}
          />
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            {t("Then...")}
          </Text>
          {stepSelect({
            value: guidance.next,
            valueChange: setNext,
            handleIndex: 2 + index,
          })}
          {!guidance.next && (
            <Text fontSize="xs" fontWeight="regular" textColor="error">
              {t("Required")}
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});
